<script lang="ts">
import { FormConfigurationProvider } from '@/app/interest-management/providers/form-configuration.provider';
import { InterestManagementController } from '@/app/interest-management/providers/interest-management.controller';
import { InterestManagementFactory } from '@/app/interest-management/providers/interest-management.factory';
import TextField from '@/components/atoms/form/form-field/TextField.vue';
import FormGroup from '@/components/atoms/form/form-group/FormGroup.vue';
import { SwitchType } from '@/components/atoms/form/switch/switch-type.definition';
import Switch from '@/components/atoms/form/switch/Switch.vue';
import IconComponent from '@/components/atoms/Icon.vue';
import Separator from '@/components/atoms/Separator.vue';
import BlockNotice from '@/components/molecules/notices/block-notice/BlockNotice.vue';
import { IconNoticeType } from '@/components/molecules/notices/icon-notice/icon-notice-type.definition';
import IconNotice from '@/components/molecules/notices/icon-notice/IconNotice.vue';
import { NoticeType } from '@/components/molecules/notices/notice-type.definition';
import PanelRow from '@/components/molecules/panel/PanelRow.vue';
import Panel from '@/components/molecules/panel/Panel.vue';
import { IconName, TranslationKey } from 'webshop-modules';
import Multiselect from '@vueform/multiselect';
import { Component, Vue, Inject } from "vue-facing-decorator"

@Component({
    name: 'InterestManagement',
    components: {
        BlockNotice,
        TextField,
        Separator,
        Switch,
        PanelRow,
        Panel,
        Multiselect,
        FormGroup,
        IconComponent,
        IconNotice,
    },
})
export default class InterestManagement extends Vue {
    @Inject() 
    public controller!: InterestManagementController;

    public readonly FormConfigurationProvider: typeof FormConfigurationProvider = FormConfigurationProvider;
    public readonly IconName: typeof IconName = IconName;
    public readonly IconNoticeType: typeof IconNoticeType = IconNoticeType;
    public readonly InterestManagementFactory: typeof InterestManagementFactory = InterestManagementFactory;
    public readonly NoticeType: typeof NoticeType = NoticeType;
    public readonly SwitchType: typeof SwitchType = SwitchType;
    public readonly Translation: typeof TranslationKey = TranslationKey;

    public interestType: { value: string; label: string; } | null = null;
}
</script>

<template>
    <Panel v-if="controller.state.authenticationError || controller.state.automaticSubscriptionNotice.state"
           :state="{ hasNoHeader: true, isContentShown: true }">
        <PanelRow :state="{}">
            <BlockNotice v-if="controller.state.authenticationError"
                :type="NoticeType.Error"
                :title="{ key: Translation.General_request_error_title }"
                :content="{ key: Translation.Interest_subscription_authentication_error }"
            ></BlockNotice>

            <BlockNotice v-if="!controller.state.authenticationError && controller.state.automaticSubscriptionNotice.state"
                         @click="controller.handleAutomaticSubscriptionNoticeInteraction()"
                         :type="controller.state.automaticSubscriptionNotice.type"
                         :title="controller.state.automaticSubscriptionNotice.title"
                         :content="controller.state.automaticSubscriptionNotice.content"></BlockNotice>
        </PanelRow>
    </Panel>

    <Panel v-if="!controller.state.authenticationError" :state="{ hasNoHeader: true, isContentShown: true }">
        <PanelRow :state="{}" v-if="!controller.state.existingUser">
            <h3 v-translate="{ key: Translation.User_enter_your_email }"></h3>
            <p v-translate="{ key: Translation.Interest_subscription_general_interest_description }"></p>

            <FormGroup
                :config="FormConfigurationProvider.emailConfig"
                :state="controller.state.email"
                @state-change="controller.updateStateEmail($event)"
            ></FormGroup>
        </PanelRow>

        <PanelRow :state="{ hasNoBorder: true, hasPaddedContent: true }" class="-is-split-row">
            <h3 v-translate="{ key: Translation.Interest_subscription_general_interest_section_title }"></h3>
            <p v-translate="{ key: Translation.Interest_subscription_general_interest_section_description }"></p>

            <ol class="m-switchList">
                <li v-for="interest in controller.state.eventimInterests" :key="interest.resourceId" class="m-switchList__item">
                    <div class="m-switchList__itemHead">
                        <h4>{{ interest.title }}</h4>
                        <Switch
                            :state="{ state: interest.state, type: SwitchType.Basic }"
                            @state-change="controller.handleInterestChange(interest, $event)"
                        ></Switch>
                    </div>

                    <div class="m-switchList__itemBody" v-if="interest.description">
                        <p>{{ interest.description }}</p>
                    </div>
                </li>
            </ol>
        </PanelRow>

        <PanelRow :state="{ hasNoBorder: true, hasNoPadding: true }" class="-is-split-row">
            <IconNotice :state="{ icon: IconName.Gdpr, type: IconNoticeType.Basic, hasSwitch: false }">
                <p v-html="controller.getDppNotice()"></p>
            </IconNotice>
        </PanelRow>

        <PanelRow :state="{}">
            <h3 v-translate="{ key: Translation.Interest_subscription_artist_section_title}"></h3>
            <p v-translate="{ key: Translation.Interest_subscription_artist_section_description }"></p>

            <ol class="m-switchList -is-condensed-list" v-if="controller.state.artistInterests.length">
                <li v-for="interest in controller.state.artistInterests"
                    :key="interest.resourceId"
                    class="m-switchList__item">
                    <div class="m-switchList__itemHead">
                        <h4>{{ interest.title }}</h4>
                        <Switch
                            :state="{ state: interest.state, type: SwitchType.Basic }"
                            @state-change="controller.handleInterestChange(interest, $event)"
                        ></Switch>
                    </div>
                </li>
            </ol>

            <Separator :label="controller.translations?.artistSeparatorLabel"></Separator>

            <div class="m-formGroup -fullwidth">
                <TextField
                    :config="{ label: controller.translations?.searchByName, name: 'artistAlarmQuery', fullwidth: true }"
                    :state="controller.state.artistInterestQuery.name"
                    @state-change="controller.updateArtistAlarmQuery($event)"
                ></TextField>
            </div>

            <ol class="m-switchList -is-condensed-list" v-if="controller.getArtistAlarmResults().length">
                <li v-for="interest in controller.getArtistAlarmResults()" :key="interest.resourceId" class="m-switchList__item">
                    <div class="m-switchList__itemHead">
                        <h4>{{ interest.title }}</h4>
                        <Switch
                            :state="{ state: interest.state, type: SwitchType.Basic }"
                            @state-change="controller.handleInterestChange(interest, $event)"
                        ></Switch>
                    </div>
                </li>
            </ol>
        </PanelRow>

        <PanelRow :state="{}">
            <h3 v-translate="{ key: Translation.Interest_subscription_topics_section_title }"></h3>
            <p v-translate="{ key: Translation.Interest_subscription_topics_section_description }"></p>

            <!-- Interest selection -->
            <ol class="m-switchList -is-condensed-list" v-if="controller.state.topicInterests.length">
                <li v-for="interest in controller.state.topicInterests" :key="interest.resourceId" class="m-switchList__item">
                    <div class="m-switchList__itemHead">
                        <h4>{{ interest.title }}</h4>
                        <Switch
                            :state="{ state: interest.state, type: SwitchType.Basic }"
                            @state-change="controller.handleInterestChange(interest, $event)"
                        ></Switch>
                    </div>
                </li>
            </ol>

            <Separator :label="controller.translations?.interestSeparatorLabel"></Separator>

            <div class="m-formGroup m-formGroup--selectAndSearch">
                <Multiselect
                    v-model="interestType"
                    :placeholder="controller.translations?.interestTypeSelectionLabel"
                    :options="InterestManagementFactory.interestTypeOptions()"
                    :object=true
                    @change="controller.handleInterestTypeChange($event)"
                ></Multiselect>

                <TextField
                    :config="{
                        label: controller.translations?.limitByName,
                        name: 'interestQuery',
                    }"
                    :state="controller.state.topicInterestQuery.name"
                    @state-change="controller.updateInterestQuery($event)"
                ></TextField>
            </div>

            <!-- Category / Venue search results -->
            <ol class="m-switchList -is-condensed-list" v-if="controller.getInterestResults().length">
                <li v-for="interest in controller.getInterestResults()" :key="interest.resourceId" class="m-switchList__item">
                    <div class="m-switchList__itemHead">
                        <h4>{{ interest.title }}</h4>
                        <Switch
                            :state="{ state: interest.state, type: SwitchType.Basic }"
                            @state-change="controller.handleInterestChange(interest, $event)"
                        ></Switch>
                    </div>
                </li>
            </ol>
        </PanelRow>

        <PanelRow v-if="controller.state.existingUser" :state="{}">
            <IconNotice
                :state="{
                    icon: IconName.BellOff,
                    type: IconNoticeType.Error,
                    hasSwitch: true,
                    switchState: {
                        state: controller.state.noContactInterest.state,
                        type: SwitchType.Error,
                    }
                }"
                @state-change="controller.handleNoContactChange(controller.state.noContactInterest, $event)"
            >
                <h4 v-translate="{ key: Translation.Customer_no_contact_title }"></h4>
                <p v-translate="{ key: Translation.Customer_no_contact_description }"></p>
            </IconNotice>
        </PanelRow>

        <PanelRow :state="{}">
            <BlockNotice v-if="controller.state.notice.state"
                         @click="controller.handleNoticeInteraction()"
                         :title="controller.state.notice.title"
                         :content="controller.state.notice.content"></BlockNotice>

            <button
                v-on:click.stop="controller.createSubscription()"
                class="a-button -submit" type="submit"
                v-bind:disabled="!controller.hasValidSubscription(controller.state)"
                v-bind:class="[{ '-active': controller.hasValidSubscription(controller.state) }]">
                <span v-if="!controller.state.existingUser" v-translate="{ key: Translation.Notifications_subscribe }"></span>
                <span v-if="controller.state.existingUser" v-translate="{ key: Translation.Save }"></span>
            </button>
        </PanelRow>
    </Panel>
</template>

<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss">
@use 'sass:math';
@import "../../../public/static/styles/@atomic/core/variables";
@import "../../../public/static/styles/@atomic/core/theme";
@import "../../../public/static/styles/@atomic/core/mixins";

.m-iconNotice__icon {
    fill: $border-color;

    .-error.-active & {
        fill: $error-text;
    }
}

.multiselect {
    border-radius: var(--border-radius);

    .multiselect-wrapper {
        border-radius: var(--border-radius);
    }

    .multiselect-dropdown {
        border-radius: 0 0 var(--border-radius) var(--border-radius);
    }

    &.is-open.is-active {
        border-radius: var(--border-radius) var(--border-radius) 0 0;
    }
}

.m-panel__body {
    @include five-col {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        padding: 0 math.div($l-hoz-space-fiveCol, 2) $l-ver-space-fiveCol*2;
    }

    .m-panel__row {
        @include five-col {
            width: 100%;
            padding-right: math.div($l-hoz-space-fiveCol, 2) !important;
            padding-left: math.div($l-hoz-space-fiveCol, 2) !important;
        }

        h3 {
            margin-bottom: 0;
        }

        h3 + p {
            @include secondary-text;
            margin-top: 0;
        }

        p + .m-switchList {
            padding-top: 0;
        }

        .m-blockNotice:first-child {
            margin-top: 0;
        }
    }

    .m-panel__row.-is-split-row {
        margin: 0;

        @include five-col {
            flex: 0 0 50%;
            margin: 0 0 math.div($l-ver-space-fiveCol, 2);
            border-top: 0 none;
        }

        .m-switchList {
            @include five-col {
                padding-top: math.div($l-ver-space-fiveCol, 2);
                padding-bottom: math.div($l-ver-space-fiveCol, 2);
            }
        }

        .m-switchList__item {
            padding: math.div($l-ver-space-fiveCol, 2) 0;
        }
    }
}

.m-formGroup--selectAndSearch {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;

    @include three-col {
        flex-direction: row;;
    }

    .multiselect {
        margin: 0;
        border-color: $border-color;

        @include three-col {
            max-width: calc(2/5 * 100%);
        }
    }

    .a-formField {
        @include element-left-margin();
        flex: 1 1 auto;
    }

    & + .m-switchList {
        @include element-top-margin(-2);
    }
}
</style>
